import { router } from "@inertiajs/react";
import { Anchor, Divider, Group, HoverCard, Loader, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { getFullName } from "../../../helpers/stringHelper";
import { useYearContext } from "../../../hooks/useYearContext";
import type { Document } from "../../../models/document";
import { PdfStatus } from "../../../models/enums/pdfStatus";
import { Role } from "../../../models/enums/role";
import { apprentice_path, rerender_document_path } from "../../../routes";
import { DataTable, type DataTableProps } from "../../datatable/DataTable";
import { DocumentViewStatusIndicator } from "../../documentView/DocumentViewStatusIndicator";
import { FileSyncIndicator } from "../../indicator/FileSyncIndicator";
import { StatusIndicator } from "../../indicator/StatusIndicator";

type DocumentTableProps = Required<
  Pick<
    DataTableProps<Document>,
    | "onEdit"
    | "onDelete"
    | "onView"
    | "onSortStatusChange"
    | "sortStatus"
    | "selectedRecords"
    | "onSelectedRecordsChange"
    | "pagination"
    | "onPageChange"
    | "fetching"
  >
> & {
  documents: Document[];
};

export function DocumentTable({ documents, onView, ...props }: DocumentTableProps) {
  const { pathWithYearContext } = useYearContext();
  return (
    <DataTable
      columns={[
        {
          accessor: "updated",
          title: "",
          width: "0%",
          render: (document) => {
            if (document.pdf_status === PdfStatus.ERROR) {
              const warnText = (
                <>
                  <Text>Es gab folgenden Fehler beim erstellen des PDF:</Text>
                  <Text>{document.pdf_message ?? "Es kann kein Fehler angezeigt werden"}</Text>
                </>
              );
              return (
                <FileSyncIndicator
                  warn
                  warnText={warnText}
                  warnIcon={<IconAlertTriangle />}
                  onWarnAction={() => router.post(pathWithYearContext(rerender_document_path, document.id))}
                />
              );
            }
            if (document.pdf_status === PdfStatus.QUEUED) {
              return (
                <HoverCard>
                  <HoverCard.Target>
                    <Loader color="zli-blue" size="sm" />
                  </HoverCard.Target>
                  <HoverCard.Dropdown>{document.pdf_message}</HoverCard.Dropdown>
                </HoverCard>
              );
            }
            if (document.report) {
              const report_date = Date.parse(document.report.updated_at);
              const document_date = Date.parse(document.updated_at);
              const warn = report_date > document_date;
              const warnText = (
                <>
                  <Text>Das PDF-Dokument ist nicht mehr synchron mit dem Bericht.</Text>
                  <Text>Drücken Sie auf den Knopf um das PDF neu zu generieren.</Text>
                </>
              );
              const okText = <Text>Das PDF-Dokument ist auf dem neusten Stand.</Text>;
              return (
                <FileSyncIndicator
                  warn={warn}
                  warnText={warnText}
                  okText={okText}
                  onWarnAction={() => router.post(pathWithYearContext(rerender_document_path, document.id))}
                />
              );
            }

            return "";
          },
        },
        {
          accessor: "document",
          title: "Dokument",
          sortable: true,
          width: "25%",
          render: (document) =>
            document.pdf_status !== PdfStatus.GENERATED ? (
              document.title
            ) : (
              <Anchor onClick={() => onView(document)}>{document.title}</Anchor>
            ),
        },
        {
          accessor: "apprentice",
          title: "Name",
          sortable: true,
          width: "25%",
          render: (document) => (
            <Anchor onClick={() => router.get(pathWithYearContext(apprentice_path, document.apprentice.id))}>
              {getFullName(document.apprentice)}
            </Anchor>
          ),
        },
        {
          accessor: "group.name",
          title: "Gruppe",
          sortable: true,
          width: "25%",
          render: (document) => (document.group ? document.group.name : "-"),
        },
        {
          accessor: "publish",
          title: "Freigegeben",
          width: "0%",
          sortable: true,
          render: (document) => <StatusIndicator active={document.publish} />,
        },
        {
          accessor: "document_views",
          title: (
            <Group ta="center" wrap="nowrap" gap={1}>
              <Tooltip label="Lernende/r">
                <Text fw={700} flex={1}>
                  L
                </Text>
              </Tooltip>
              <Divider orientation="vertical" />
              <Tooltip label="Berufsbildner/innen">
                <Text fw={700} flex={1}>
                  B
                </Text>
              </Tooltip>
              <Divider orientation="vertical" />
              <Tooltip label="Elternteil 1">
                <Text fw={700} flex={1}>
                  E1
                </Text>
              </Tooltip>
              <Divider orientation="vertical" />
              <Tooltip label="Elternteil 2">
                <Text fw={700} flex={1}>
                  E2
                </Text>
              </Tooltip>
            </Group>
          ),
          width: "10%",
          render: (document) => (
            <Group wrap="nowrap" gap={1}>
              <DocumentViewStatusIndicator
                flex={1}
                documentViewStatus={document.group_document_views.apprentice}
                read={document.document_readable.includes(Role.APPRENTICE)}
                sign={document.document_signable.includes(Role.APPRENTICE)}
              />
              <Divider orientation="vertical" />
              <DocumentViewStatusIndicator
                flex={1}
                documentViewStatus={document.group_document_views.apprentice_supervisor}
                sign={document.document_signable.includes(Role.APPRENTICE_SUPERVISOR)}
                read={document.document_readable.includes(Role.APPRENTICE_SUPERVISOR)}
                disabled={!document.apprentice.apprentice_supervisor}
              />
              <Divider orientation="vertical" />
              <DocumentViewStatusIndicator
                flex={1}
                documentViewStatus={document.group_document_views.parent1}
                sign={document.document_signable.includes(Role.PARENT)}
                read={document.document_readable.includes(Role.PARENT)}
                disabled={document.apprentice.parents.length < 1}
              />
              <Divider orientation="vertical" />
              <DocumentViewStatusIndicator
                flex={1}
                documentViewStatus={document.group_document_views.parent2}
                sign={document.document_signable.includes(Role.PARENT)}
                read={document.document_readable.includes(Role.PARENT)}
                disabled={document.apprentice.parents.length < 2}
              />
            </Group>
          ),
        },
      ]}
      records={documents}
      onView={onView}
      {...props}
    />
  );
}
